import { useEffect, useMemo, useState } from 'react';
import { CreditCard, Landmark, Loader2, Plus, AlertCircle } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@shared/ui/radio-group';
import { Label } from '@shared/ui/label';
import { billingApi } from '@shared/services/api/billingApi';

const METHOD_CARD = 1;
const STATUS_ACTIVE = 1;
const STATUS_PENDING_VERIFICATION = 2;

// Same card or bank account across two fetches, whose tokens always differ.
const isSameMethod = (a, b) =>
  a.methodType === b.methodType
  && a.lastFour === b.lastFour
  && (a.brand ?? null) === (b.brand ?? null)
  && (a.expMonth ?? null) === (b.expMonth ?? null)
  && (a.expYear ?? null) === (b.expYear ?? null)
  && (a.bankName ?? null) === (b.bankName ?? null);

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const formatExpiry = (m, y) => (m && y ? `${String(m).padStart(2, '0')}/${String(y).slice(-2)}` : '');
const buildTestId = (dataTestId, suffix) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

function buildLabel(m) {
  if (m.methodType === METHOD_CARD) {
    return `${capitalize(m.brand) || 'Card'} ending in ${m.lastFour}`;
  }
  const accountType = m.accountType ? ` ${capitalize(m.accountType)}` : '';
  return m.bankName ? `${m.bankName}${accountType} ending in ${m.lastFour}` : `Bank${accountType} ending in ${m.lastFour}`;
}

function getBrandMark(cardBrand) {
  if (cardBrand === 'visa') {
    return <span className="text-[#1A1F71] font-bold text-lg italic tracking-tight">VISA</span>;
  }
  if (cardBrand === 'mastercard') {
    return <span className="text-[#EB001B] font-bold text-sm">mastercard</span>;
  }
  if (cardBrand === 'amex') {
    return <span className="text-[#006FCF] font-bold text-sm">AMEX</span>;
  }
  return <CreditCard className="h-6 w-6 text-fg-muted" />;
}

function PaymentMethodOption({ method, value, disabled, dataTestId }) {
  const id = `pm-${method.token.slice(0, 16)}`;
  const isPending = method.status === STATUS_PENDING_VERIFICATION;
  const methodTestId = dataTestId ? `${dataTestId}-item-${method.token.slice(0, 8)}` : undefined;
  const isCard = method.methodType === METHOD_CARD;
  const cardBrand = method.brand?.toLowerCase();
  const brandMark = getBrandMark(cardBrand);

  return (
    <Label
      htmlFor={id}
      className={`flex cursor-pointer items-center gap-3 rounded-xl border bg-surface-panel p-4 transition-colors ${
        value === method.token ? 'border-brand ' : 'border-line-field hover:border-brand'
      } ${isPending || disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      data-testid={methodTestId}
    >
      <RadioGroupItem id={id} value={method.token} disabled={isPending || disabled} className="border-line-input data-[state=checked]:border-brand data-[state=checked]:bg-brand" data-testid={methodTestId ? `${methodTestId}-radio` : undefined} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-lg font-semibold text-fg" data-testid={methodTestId ? `${methodTestId}-label` : undefined}>
            {isCard ? `${capitalize(method.brand) || 'Card'} •••• ${method.lastFour}` : buildLabel(method)}
          </span>
        </div>
        {isCard && method.expMonth && method.expYear && (
          <p className="text-sm text-stat-label mt-0.5" data-testid={methodTestId ? `${methodTestId}-expiry` : undefined}>Expires {formatExpiry(method.expMonth, method.expYear)}</p>
        )}
      </div>
      {/* Card Brand Logo or Icon */}
      <div className="flex-shrink-0">
        {isCard ? (
          <div className="flex items-center justify-center">
            {brandMark}
          </div>
        ) : (
          <Landmark className="h-6 w-6 text-fg-muted" />
        )}
      </div>
    </Label>
  );
}

/**
 * Reusable saved-payment-method picker. Loads the firm's live methods via billingApi,
 * lets the caller filter by methodType, surfaces ACH "pending verification" state, and
 * (optionally) renders an "Add new" affordance that calls the supplied onAddNew handler.
 *
 * @param {object} props
 * @param {string|null} props.value                       Currently selected token (or null).
 * @param {(token: string, method: object|null) => void} props.onChange  Called with the selected token and its method record.
 * @param {1 | 2 | undefined} [props.methodTypeFilter]    1 = cards only, 2 = ACH only, undefined = all.
 * @param {boolean} [props.disabled]                      Disables interaction.
 * @param {boolean} [props.showAddNew]                    Renders an "+ Add new payment method" row.
 * @param {() => void} [props.onAddNew]                   Invoked when the user clicks the Add row.
 * @param {boolean} [props.preferDefault]                 If true, auto-selects the Primary method on first load.
 * @param {object|null} [props.preferMethod]              With preferDefault, auto-selects this method instead when it is
 *                                                        listed and active (matched by card / bank details).
 */
export function PaymentMethodSelector({
  value,
  onChange,
  methodTypeFilter,
  disabled = false,
  showAddNew = false,
  onAddNew,
  preferDefault = true,
  preferMethod = null,
  dataTestId,
}) {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await billingApi.getPaymentMethods();
        if (cancelled) return;
        if (res?.success) {
          setMethods(res.data || []);
        } else {
          setError(res?.message || 'Failed to load payment methods.');
        }
      } catch (err) {
        if (cancelled) return;
        const status = err?.response?.status;
        const code = err?.response?.data?.errorCode;
        if (status === 409 && code === 'STRIPE_CUSTOMER_NOT_PROVISIONED') {
          setError('Your billing account is still being set up. Please try again in a minute.');
        } else {
          setError(err?.response?.data?.message || err?.message || 'Failed to load payment methods.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => {
    if (!methodTypeFilter) return methods;
    return methods.filter((m) => m.methodType === methodTypeFilter);
  }, [methods, methodTypeFilter]);

  useEffect(() => {
    if (!preferDefault || value || visible.length === 0) return;
    const preferred = (preferMethod && visible.find((m) => m.status === STATUS_ACTIVE && isSameMethod(m, preferMethod)))
      ?? visible.find((m) => m.isDefault && m.status === STATUS_ACTIVE)
      ?? visible.find((m) => m.status === STATUS_ACTIVE);
    // Hand back the record too, like a manual pick does: callers need methodType to decide
    // whether a card CVV re-verification applies (DirectPaymentDialog, SEC-41).
    if (preferred) onChange?.(preferred.token, preferred);
  }, [visible, preferDefault, preferMethod, value, onChange]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-fg-muted" data-testid={buildTestId(dataTestId, 'loading')}>
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading payment methods…
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-lg border border-danger-line bg-danger-bg p-3 text-sm text-danger-fg" data-testid={buildTestId(dataTestId, 'error')}>
        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <span data-testid={buildTestId(dataTestId, 'error-message')}>{error}</span>
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-line-field bg-surface-panel p-4 text-sm" data-testid={buildTestId(dataTestId, 'empty')}>
        <p className="text-fg-navy font-medium" data-testid={buildTestId(dataTestId, 'empty-message')}>No saved payment methods.</p>
        {showAddNew && (
          <button
            type="button"
            onClick={onAddNew}
            disabled={disabled}
            className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-brand-solid hover:underline disabled:opacity-50"
            data-testid={buildTestId(dataTestId, 'add-new-button')}
          >
            <Plus className="h-4 w-4" />
            Add a payment method
          </button>
        )}
      </div>
    );
  }

  // Callback signature: onChange(token, method). The method object is included so callers
  // can store the full record locally rather than re-deriving from a stale list — important
  // because each Mint() on the backend produces a fresh HMAC token even for the same pm_xxx,
  // so caching tokens across separate fetches breaks find-by-token lookups.
  const handleChange = (token) => {
    const picked = visible.find((m) => m.token === token) || null;
    onChange?.(token, picked);
  };

  return (
    <RadioGroup
      value={value ?? ''}
      onValueChange={handleChange}
      disabled={disabled}
      className="gap-2"
      aria-label="Select a payment method"
      data-testid={dataTestId}
    >
      {visible.map((method) => (
        <PaymentMethodOption
          key={method.token}
          method={method}
          value={value}
          disabled={disabled}
          dataTestId={dataTestId}
        />
      ))}

      {showAddNew && (
        <button
          type="button"
          onClick={onAddNew}
          disabled={disabled}
          className="mt-1 inline-flex items-center gap-2 self-start text-sm font-semibold text-brand-solid hover:underline disabled:opacity-50"
          data-testid={buildTestId(dataTestId, 'add-new-button-bottom')}
        >
          <Plus className="h-4 w-4" />
          Add new payment method
        </button>
      )}
    </RadioGroup>
  );
}
